/**
 * Per-model control + status.
 *
 * The phase is spelled out rather than reduced to a dot, because the states
 * differ in what the user should DO: "off" is fine and free, "starting" needs
 * patience (~4 min, and the reason is worth saying), "ready" is billing.
 */

import { FiPlay, FiSquare } from "react-icons/fi";
import { Button, Card } from "@/components/ui";
import type { ModelInfo, Status } from "@/lib/types";

const PHASE: Record<string, { dot: string; text: string; label: string }> = {
  off:      { dot: "bg-fh-text-muted",                 text: "text-fh-text-secondary", label: "Off" },
  starting: { dot: "bg-fh-priority-normal animate-pulse", text: "text-fh-priority-normal", label: "Starting" },
  ready:    { dot: "bg-fh-positive",                   text: "text-fh-positive",       label: "Ready" },
  stopping: { dot: "bg-fh-text-muted animate-pulse",   text: "text-fh-text-secondary", label: "Stopping" },
  error:    { dot: "bg-fh-danger",                     text: "text-fh-danger",         label: "Error" },
};

export function ModelBar({ models, status, onStart, onStop }: {
  models: ModelInfo[];
  status: Record<string, Status>;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {models.map((m) => {
        const s = status[m.id];
        const p = PHASE[s?.phase ?? "off"] ?? PHASE.off;
        const busy = s?.phase === "starting" || s?.phase === "stopping";
        return (
          <Card key={m.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${p.dot}`} />
                  <span className="font-medium text-fh-text-strong">{m.label}</span>
                  <span className={`text-xs ${p.text}`}>{p.label}</span>
                </div>
                <p className="mt-1 text-fh-meta leading-relaxed text-fh-text-secondary">
                  {s?.detail ?? "—"}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" className="gap-1.5" onClick={() => onStart(m.id)}
                  disabled={busy || s?.phase === "ready"}>
                  <FiPlay /> Start
                </Button>
                <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => onStop(m.id)}
                  disabled={busy || s?.phase === "off"}>
                  <FiSquare /> Stop
                </Button>
              </div>
            </div>
            <p className="mt-3 border-t border-fh-border-soft pt-2 text-fh-meta leading-relaxed text-fh-text-muted">
              {m.notes}
            </p>
          </Card>
        );
      })}
    </div>
  );
}
